import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {findUniqueChannelType} from '../../helpers/models/channelHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {RuleBundleTypesEnum} from '../../models/api/ruleCommonTypes';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../models/url/settingsUrlModel';
import {getFlatChannels, makeGetChannelsForInboxId} from '../../store/data/channels/channelsSelectors';
import {getExternalChannelTypes} from '../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {
  getFlatInboxes,
  getInboxes,
  makeGetInboxesByNamespace,
} from '../../store/data/inboxes/inboxesSelectors';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormField,
  StyledSettingsFormMain,
  StyledSettingsFormTextAreaBox,
  StyledSettingsFormTextBox,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {RuleComplexRecipeTypesEnum} from '../rules/ruleRecipeModel';
import {getInboxFedByActions, inboxesImpactedByCompoundConditions} from '../rules/ruleSimulationHelpers';
import {InboxGraphInheritableProps} from './graph/inboxGraphTypes';
import {useSettingsInboxesBanners} from './settingsInboxesCommon';
import {SettingsInboxesInboxHeader} from './settingsInboxesInboxHeader';
import {InboxTraffic} from './settingsInboxTraffic';

/*
 * Props.
 */

interface SettingsInboxesOverviewProps {
  url: SettingsInboxesUrl;
  inbox: InboxModel;
  onInboxUpdate: (inbox: InboxModel) => Promise<void>;
}

/*
 * Component.
 */

export const SettingsInboxOverview: FC<SettingsInboxesOverviewProps> = (props) => {
  const {url, inbox, onInboxUpdate} = props;
  const [isDirty, setIsDirty] = useState(false);
  const [name, setName] = useState(inbox.name || '');
  const [description, setDescription] = useState(inbox.description || '');
  const {navigateToLink} = useLinkNavigation();
  const {showUpdateBanner} = useSettingsInboxesBanners();
  const inboxes = useAppSelector(getInboxes);
  const getChannelsForInbox = makeGetChannelsForInboxId(() => inbox.id);
  const channelsForInbox = useAppSelector(getChannelsForInbox);

  const onCancelClick = () => {
    setName(inbox.name || '');
    setDescription(inbox.description || '');
    setIsDirty(false);
  };
  const onOkClick = async () => {
    if (!inbox) {
      return;
    }
    await onInboxUpdate({
      ...inbox,
      name,
      description,
    });
    showUpdateBanner(name);
    setIsDirty(false);
  };
  const canSave = useMemo(() => name.trim().length > 0, [name]);
  const localize = useLocalize();
  const externalChannelTypes = useAppSelector(getExternalChannelTypes);

  const getInboxesInNamespace = useMemo(
    () => makeGetInboxesByNamespace(() => url.namespace),
    [url.namespace],
  );
  const inboxesInNamespace = useAppSelector(getInboxesInNamespace);
  const allChannels = useAppSelector(getFlatChannels);
  const allInboxes = useAppSelector(getFlatInboxes);

  const inboxGraphInheritableProps: InboxGraphInheritableProps = useMemo(
    () => ({
      listImpactedInboxes: (compoundConditions) =>
        inboxesImpactedByCompoundConditions(compoundConditions, allInboxes, allChannels),
      getFedInbox: (actions) => getInboxFedByActions(actions, inboxesInNamespace),
      getInboxById(id) {
        return inboxes[id];
      },
      channels: channelsForInbox,
      getUniqueChannelType: (channel) => findUniqueChannelType(channel, externalChannelTypes),
      goToChannel: ({channelId}) => {
        // TODO(damien): replace with `buildSettingsPath` calls whenever new pages are done
        navigateToLink(`/settings/${url.namespace}/inboxes/edit/${inbox.id}/channels/${channelId}`);
      },
      goToInbox: (inboxId) => {
        navigateToLink(
          buildSettingsPath({
            namespace: url.namespace,
            inboxId,
            rootPage: SettingsRootPagesEnum.INBOXES,
            resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          }),
        );
      },
      goToInboxRules: () => {
        // TODO(damien): replace with `buildSettingsPath` calls whenever new pages are done
        navigateToLink(`/settings/${url.namespace}/inboxes/edit/${inbox.id}/rules`);
      },
      goToBundle: (bundle) => {
        // SLA Recipe.
        if (bundle.type === RuleBundleTypesEnum.SLA) {
          navigateToLink(
            buildSettingsPath({
              rootPage: SettingsRootPagesEnum.RULES,
              namespace: url.namespace,
              resourcePage: SettingsResourcePagesEnum.EDIT,
              bundleId: bundle.id,
              filters: undefined,
              inboxId: undefined,
              recipe: RuleComplexRecipeTypesEnum.SLA,
              isInboxCreation: false,
              ruleId: bundle.rules[0].id,
            }),
          );
          return;
        }

        // CSAT Recipe.
        if (bundle.type === RuleBundleTypesEnum.CSAT) {
          navigateToLink(
            buildSettingsPath({
              rootPage: SettingsRootPagesEnum.RULES,
              namespace: url.namespace,
              resourcePage: SettingsResourcePagesEnum.EDIT,
              bundleId: bundle.id,
              filters: undefined,
              inboxId: undefined,
              recipe: RuleComplexRecipeTypesEnum.CSAT,
              isInboxCreation: false,
              ruleId: bundle.rules[0].id,
            }),
          );
          return;
        }

        // Required Tagging Recipe.
        if (bundle.type === RuleBundleTypesEnum.REQUIRED_TAGGING) {
          navigateToLink(
            buildSettingsPath({
              rootPage: SettingsRootPagesEnum.RULES,
              namespace: url.namespace,
              resourcePage: SettingsResourcePagesEnum.EDIT,
              bundleId: bundle.id,
              filters: undefined,
              inboxId: undefined,
              recipe: RuleComplexRecipeTypesEnum.REQUIRED_TAGGING,
              isInboxCreation: false,
              ruleId: bundle.rules[0].id,
            }),
          );
          return;
        }

        // Regular single rule.
        const rule = bundle.rules[0];

        // Should never happen.
        if (!rule.id) {
          return;
        }

        navigateToLink(
          buildSettingsPath({
            rootPage: SettingsRootPagesEnum.RULES,
            namespace: url.namespace,
            resourcePage: SettingsResourcePagesEnum.EDIT,
            bundleId: bundle.id,
            filters: undefined,
            inboxId: undefined,
            recipe: bundle.type,
            isInboxCreation: false,
            ruleId: bundle.rules[0].id,
          }),
        );
      },
    }),
    [
      allChannels,
      allInboxes,
      channelsForInbox,
      externalChannelTypes,
      inbox.id,
      inboxes,
      inboxesInNamespace,
      navigateToLink,
      url.namespace,
    ],
  );

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!canSave}>
          <Trans id="settingstags-saveinbox">Save</Trans>
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <StyledSettingsWrapper>
      <SettingsInboxesInboxHeader url={url} inbox={inbox} />
      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <StyledSettingsFormField>
            <StyledSettingsFormTextBox
              $width={224}
              label={<Trans id="inboxeditor-name">Name</Trans>}
              value={name}
              onChange={(newName) => {
                setIsDirty(true);
                setName(newName);
              }}
              isRequired
            />
          </StyledSettingsFormField>
          <StyledSettingsFormField>
            <StyledSettingsFormTextAreaBox
              $width={480}
              $height={70}
              value={description}
              onChange={(newDescription) => {
                setIsDirty(true);
                setDescription(newDescription);
              }}
              label={<Trans id="inboxeditor-description">Description</Trans>}
              placeholder={localize(
                t('inboxeditor-descriptionplaceholder')`What will your team use this inbox for?`,
              )}
            />
          </StyledSettingsFormField>
          <InboxTraffic
            inbox={inbox}
            listImpactedInboxes={inboxGraphInheritableProps.listImpactedInboxes}
            getFedInbox={inboxGraphInheritableProps.getFedInbox}
            goToChannel={inboxGraphInheritableProps.goToChannel}
            goToInboxRules={inboxGraphInheritableProps.goToInboxRules}
            goToInbox={inboxGraphInheritableProps.goToInbox}
            goToBundle={inboxGraphInheritableProps.goToBundle}
            channels={inboxGraphInheritableProps.channels}
            getUniqueChannelType={inboxGraphInheritableProps.getUniqueChannelType}
            getInboxById={inboxGraphInheritableProps.getInboxById}
          />
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};
